import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useCart, formatBRL } from "@/context/CartContext";
import { useAppConfig } from "@/context/AppConfigContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, ShoppingCart, Minus, Plus, PackageX, Truck, Heart, ShieldCheck } from "lucide-react";
import ShopComingSoon from "@/components/ShopComingSoon";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { shopEnabled, loading: cfgLoading } = useAppConfig();
  const [product, setProduct] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    if (!shopEnabled) return;
    api.get(`/shop/products/${slug}`)
      .then(({ data }) => {
        setProduct(data);
        const firstAvailable = (data.variants || []).find((v) => v.stock > 0);
        setSelectedSize(firstAvailable ? firstAvailable.size : (data.variants?.[0]?.size || null));
      })
      .catch(() => setProduct(false));
  }, [slug, shopEnabled]);

  if (cfgLoading) return null;
  if (!shopEnabled) return <ShopComingSoon />;

  if (product === null) {
    return (
      <div className="container-cuidar py-16">
        <div className="animate-pulse space-y-4">
          <div className="h-6 w-40 bg-[hsl(var(--muted))] rounded-full" />
          <div className="grid md:grid-cols-2 gap-8">
            <div className="aspect-square bg-[hsl(var(--muted))] rounded-3xl" />
            <div className="space-y-4">
              <div className="h-10 bg-[hsl(var(--muted))] rounded-xl w-3/4" />
              <div className="h-4 bg-[hsl(var(--muted))] rounded-xl w-full" />
              <div className="h-4 bg-[hsl(var(--muted))] rounded-xl w-5/6" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (product === false) {
    return (
      <div className="container-cuidar py-24 text-center">
        <h1 className="font-heading text-3xl font-extrabold">Produto não encontrado</h1>
        <Link to="/produtos" className="mt-6 inline-flex items-center gap-2 text-[hsl(var(--primary))] font-semibold hover:underline">
          <ArrowLeft className="h-4 w-4" /> Voltar para a loja
        </Link>
      </div>
    );
  }

  const soldOut = product.sold_out;
  const variantMap = new Map((product.variants || []).map((v) => [v.size, v.stock]));
  const availableStock = selectedSize ? (variantMap.get(selectedSize) || 0) : 0;
  const img = (product.images || [])[0];

  const handleAdd = () => {
    if (!selectedSize) {
      toast.error("Escolha um tamanho.");
      return;
    }
    if (availableStock <= 0) {
      toast.error("Tamanho esgotado no momento.");
      return;
    }
    if (qty > availableStock) {
      toast.error(`Só temos ${availableStock} em estoque.`);
      return;
    }
    addItem(product, selectedSize, qty);
    toast.success("Adicionado ao carrinho!");
    navigate("/carrinho");
  };

  return (
    <div className="container-cuidar py-10 sm:py-14">
      <Link to="/produtos" className="inline-flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]" data-testid="product-back-link">
        <ArrowLeft className="h-4 w-4" /> Voltar para a loja
      </Link>

      <div className="mt-6 grid gap-10 md:grid-cols-2">
        {/* Image */}
        <div className="relative aspect-square rounded-3xl overflow-hidden border border-[hsl(var(--border))] bg-[hsl(var(--muted))]">
          {img ? (
            <img src={img} alt={product.name} className={`w-full h-full ${product.category === "Kits" ? "object-contain bg-white" : "object-cover"}`} />
          ) : (
            <div className="w-full h-full grid place-items-center text-[hsl(var(--muted-foreground))]">Sem imagem</div>
          )}
          {soldOut && (
            <div className="absolute top-4 left-4 rounded-full bg-black/85 text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 flex items-center gap-1.5">
              <PackageX className="h-3.5 w-3.5" /> Esgotado
            </div>
          )}
        </div>

        {/* Details */}
        <div>
          <Badge variant="secondary" className="rounded-full">{product.category}</Badge>
          <h1 className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold leading-tight" data-testid="product-name">
            {product.name}
          </h1>
          <div className="mt-4 font-heading text-4xl font-extrabold text-[hsl(var(--primary))]" data-testid="product-price">
            {formatBRL(product.price_cents)}
          </div>

          <p className="mt-5 text-[hsl(var(--muted-foreground))] leading-relaxed">
            {product.description}
          </p>

          {/* Sizes */}
          <div className="mt-6">
            <div className="text-sm font-semibold mb-2">Tamanho</div>
            <div className="flex flex-wrap gap-2" data-testid="product-size-list">
              {(product.variants || []).map((v) => {
                const isSelected = selectedSize === v.size;
                const disabled = v.stock <= 0;
                return (
                  <button
                    key={v.size}
                    type="button"
                    disabled={disabled}
                    onClick={() => setSelectedSize(v.size)}
                    data-testid={`product-size-${v.size}`}
                    className={`relative min-w-[3rem] h-11 px-4 rounded-full border text-sm font-semibold transition-all
                      ${isSelected
                        ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))] text-white shadow"
                        : "border-[hsl(var(--border))] bg-white hover:border-[hsl(var(--primary))]/40"}
                      ${disabled ? "opacity-40 cursor-not-allowed line-through" : ""}
                    `}
                  >
                    {v.size}
                  </button>
                );
              })}
            </div>
            {selectedSize && (
              <div className="mt-2 text-xs text-[hsl(var(--muted-foreground))]" data-testid="product-stock-info">
                {availableStock > 0 ? `${availableStock} em estoque` : "Tamanho esgotado"}
              </div>
            )}
          </div>

          {/* Qty */}
          <div className="mt-5">
            <div className="text-sm font-semibold mb-2">Quantidade</div>
            <div className="inline-flex items-center rounded-full border border-[hsl(var(--border))] overflow-hidden">
              <button
                type="button"
                className="p-2.5 hover:bg-[hsl(var(--muted))]"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                data-testid="product-qty-dec"
              >
                <Minus className="h-4 w-4" />
              </button>
              <span className="w-12 text-center font-heading font-bold" data-testid="product-qty-value">{qty}</span>
              <button
                type="button"
                className="p-2.5 hover:bg-[hsl(var(--muted))]"
                onClick={() => setQty((q) => Math.min(20, q + 1))}
                data-testid="product-qty-inc"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-7">
            <Button
              size="lg"
              disabled={soldOut || !selectedSize || availableStock <= 0}
              onClick={handleAdd}
              data-testid="product-add-to-cart-btn"
              className="btn-pill h-14 w-full sm:w-auto text-base bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90 disabled:bg-[hsl(var(--muted-foreground))]/40"
            >
              <ShoppingCart className="mr-2 h-5 w-5" />
              {soldOut ? "Esgotado — em breve novos lotes" : "Adicionar ao carrinho"}
            </Button>
          </div>

          {/* Benefits */}
          <div className="mt-8 grid sm:grid-cols-3 gap-3 text-xs">
            <div className="flex items-start gap-2 rounded-2xl bg-[hsl(var(--muted))] p-3">
              <Heart className="h-4 w-4 text-[hsl(var(--primary))] shrink-0 mt-0.5" />
              <span>100% da renda vai para o instituto</span>
            </div>
            <div className="flex items-start gap-2 rounded-2xl bg-[hsl(var(--muted))] p-3">
              <Truck className="h-4 w-4 text-[hsl(var(--primary))] shrink-0 mt-0.5" />
              <span>Envio via Correios para todo o Brasil</span>
            </div>
            <div className="flex items-start gap-2 rounded-2xl bg-[hsl(var(--muted))] p-3">
              <ShieldCheck className="h-4 w-4 text-[hsl(var(--primary))] shrink-0 mt-0.5" />
              <span>Pagamento seguro via Stripe</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
